import React, { useState, useEffect, useRef } from "react";
import { Tenants } from "../../../../service";
import { toast } from "react-hot-toast";
import ButtonComponent from "../../../Ui/Button";
import Modal from "../../../Ui/Modal";
import { Formik, Form } from "formik";
import FormField from "../../VendorOnboarding/FormField";
import * as Yup from "yup";
import useApiHandler from "../../../hooks/ApiHandler";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Box,
  TextField,
} from "@mui/material";

const TenantsInvitation = ({
  setModalState,
  setPropertyId,
  tenantsInfo,
  propertyId,
  onSuccess,
}) => {
  const formikSubmitRef = useRef(null);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [error, setError] = useState("");
  const [editingTenants, setEditingTenants] = useState({});
  const [tenantData, setTenantData] = useState({});
  const { callApi, loading } = useApiHandler();

  // Memoize tenants data to prevent infinite re-renders
  const tenants = React.useMemo(() => {
    return (
      tenantsInfo?.map((tenant, index) => ({
        id: tenant.id || index + 1,
        tenant_id: tenant.tenant_id || tenant.id,
        name: tenant.tenant_name,
        unit: tenant.unit, // Keep ID for payload
        unitLabel: tenant.unitLabel || tenant.unit_label || tenant.unit_apt || tenant.unit, // Use for display
        email: tenant.tenant_email,
      })) || []
    );
  }, [tenantsInfo]);

  // Initialize all tenants as selected
  useEffect(() => {
    if (tenants.length > 0) {
      setSelectedTenants(tenants.map((tenant) => tenant.id));
      setSelectAll(true);

      // Initialize tenant data for editing
      const initialData = {};
      tenants.forEach((tenant) => {
        initialData[tenant.id] = {
          tenant_name: tenant.name,
          unit: tenant.unitLabel, // Use label for display field
          tenant_email: tenant.email,
        };
      });
      setTenantData(initialData);
    }
  }, [tenants]);

  const handleClose = () => {
    setPropertyId(null);
    setModalState("");
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTenants([]);
      setSelectAll(false);
    } else {
      setSelectedTenants(tenants.map((tenant) => tenant.id));
      setSelectAll(true);
    }
    // Clear error when user makes a selection
    setError("");
  };

  const handleSelectTenant = (tenantId) => {
    if (selectedTenants.includes(tenantId)) {
      setSelectedTenants(selectedTenants.filter((id) => id !== tenantId));
      setSelectAll(false);
    } else {
      setSelectedTenants([...selectedTenants, tenantId]);
      if (selectedTenants.length + 1 === tenants.length) {
        setSelectAll(true);
      }
    }
    // Clear error when user makes a selection
    setError("");
  };

  const handleEditTenant = (tenantId) => {
    setEditingTenants((prev) => ({
      ...prev,
      [tenantId]: true,
    }));
  };

  const handleCancelEdit = (tenantId) => {
    setPropertyId(null);
    setEditingTenants((prev) => ({
      ...prev,
      [tenantId]: false,
    }));

    // Reset to original values
    const tenant = tenants.find((t) => t.id === tenantId);
    if (tenant) {
      setTenantData((prev) => ({
        ...prev,
        [tenantId]: {
          tenant_name: tenant.name,
          unit: tenant.unitLabel,
          tenant_email: tenant.email,
        },
      }));
    }
  };

  const handleSaveTenant = async (tenantId) => {
    const tenant = tenants.find((t) => t.id === tenantId);
    if (!tenant) return;

    const values = tenantData[tenantId];

    try {
      await callApi(
        () => Tenants.editTenantInvitation(tenant.tenant_id, values),
        {
          successMessage: "Tenant information updated successfully",
          errorMessage: "Failed to update tenant information",
          onSuccess: () => {
            setEditingTenants((prev) => ({
              ...prev,
              [tenantId]: false,
            }));
            toast.success("Tenant information updated successfully");
          },
          onError: (error) => {
            console.error("Error updating tenant:", error);
            toast.error("Failed to update tenant information");
          },
        }
      );
    } catch (error) {
      console.error("Error updating tenant:", error);
      toast.error("Failed to update tenant information");
    }
  };

  const handleInputChange = (tenantId, field, value) => {
    setTenantData((prev) => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        [field]: value,
      },
    }));
  };

  const handleSendInvites = async () => {
    if (selectedTenants.length === 0) {
      setError("Please select at least one tenant to send invites");
      return;
    }

    // Clear any previous errors
    setError("");

    // Get selected tenant data and format it according to API requirements
    const selectedTenantData = tenants.filter((tenant) =>
      selectedTenants.includes(tenant.id)
    );

    // Format the payload with the required structure
    const payload = {
      invitations: selectedTenantData.map((tenant) => ({
        property: propertyId,
        tenant_name: tenant.name,
        tenant_email: tenant.email,
        unit: tenant.unit,
      })),
    };

    // Use ApiCallHandler hook to make the API call
    await callApi(() => Tenants.tenantInvitation(payload), {
      successMessage: `Invites sent to ${selectedTenants.length} tenant(s)`,
      errorMessage: "Failed to send invitations. Please try again.",
      onSuccess: () => {
        setPropertyId(null);
        setModalState("");
        handleClose();
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        console.error("Error sending tenant invitations:", error);
        setError("Failed to send invitations. Please try again.");
      },
    });
  };

  const renderEditableCell = (tenant, field, label) => {
    const isEditing = editingTenants[tenant.id];
    const value = tenantData[tenant.id]?.[field] || "";

    if (isEditing) {
      return (
        <TextField
          size="small"
          value={value}
          onChange={(e) => handleInputChange(tenant.id, field, e.target.value)}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
              height: "32px",
            },
          }}
        />
      );
    }

    return (
      <Typography variant="body2" sx={{ color: "#111827", fontSize: "14px" }}>
        {value}
      </Typography>
    );
  };

  const renderActionsCell = (tenant) => {
    const isEditing = editingTenants[tenant.id];

    if (isEditing) {
      return (
        <div className="flex gap-2">
          <ButtonComponent
            size="small"
            paddingY="4px"
            paddingX="8px"
            bgColor="#3CAB5C"
            color="#FFFFFF"
            onClick={() => handleSaveTenant(tenant.id)}
            disabled={loading}
          >
            Save
          </ButtonComponent>
          <ButtonComponent
            size="small"
            paddingY="4px"
            paddingX="8px"
            bgColor="#F8F8F8"
            color="#242424"
            onClick={() => handleCancelEdit(tenant.id)}
          >
            Cancel
          </ButtonComponent>
        </div>
      );
    }

    return (
      <ButtonComponent
        size="small"
        paddingY="4px"
        paddingX="8px"
        bgColor="#F8F8F8"
        color="#242424"
        // onClick={() => handleEditTenant(tenant.id)}
      >
        Edit
      </ButtonComponent>
    );
  };

  return (
    <Modal
      style={{ maxWidth: "900px", width: "100%" }}
      open={true}
      onClose={handleClose}
      title="Invite Your Tenants To Join Properteasy"
      footer={
        <div className="flex w-full justify-between items-center sm2:flex-row flex-col">
          <div className="flex items-center">
            <Checkbox
              checked={selectAll}
              onChange={handleSelectAll}
              sx={{
                color: "#3CAB5C",
                "&.Mui-checked": {
                  color: "#3CAB5C",
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#333333" }}
            >
              Select All
            </Typography>
          </div>

          <div className="flex gap-4">
            <ButtonComponent
              paddingY="0px"
              onClick={() => setModalState("addTenant")}
            >
              Back
            </ButtonComponent>
            <ButtonComponent
              paddingY="10px"
              bgColor="#3CAB5C"
              color="#FFFFFF"
              onClick={handleSendInvites}
              disabled={selectedTenants.length === 0 || loading}
            >
              {loading ? "Sending..." : "Send Invites"}
            </ButtonComponent>
          </div>
        </div>
      }
    >
      <Box sx={{ width: "100%" }}>
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                <TableCell sx={{ width: "60px" }}></TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}
                >
                  Tenant Name
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}
                >
                  Unit
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow
                  key={tenant.id}
                  sx={{
                    "&:hover": { backgroundColor: "#f9fafb" },
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedTenants.includes(tenant.id)}
                      onChange={() => handleSelectTenant(tenant.id)}
                      sx={{
                        color: "#3CAB5C",
                        "&.Mui-checked": {
                          color: "#3CAB5C",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(tenant, "tenant_name", "Tenant Name")}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(tenant, "unit", "Unit")}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(tenant, "tenant_email", "Email")}
                  </TableCell>
                  <TableCell>{renderActionsCell(tenant)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Error message display */}
        {error && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography
              variant="body2"
              sx={{
                color: "#d32f2f",
                fontSize: "14px",
                fontWeight: 500,
                marginBottom: "10px",
              }}
            >
              {error}
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default TenantsInvitation;
